import type { MarketQuote } from "@lossdog/job-watchlists";
import { Typography } from "@povio/ui";
import type { ColumnDef, Header, Table } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MarketQuotePrice } from "@/components/stocks/MarketQuotePrice";
import TableCell from "@/components/ui/table/TableCell";
import { VirtualizedTable } from "@/components/ui/table/VirtualizedTable";
import WatchlistTableHeaderCell from "@/components/watchlist/components/WatchlistTableHeaderCell";
import { useJobSalaryContext } from "@/data/watchlist/JobSalary.context";
import { DemoContext } from "@/providers/demo/DemoContext";

export const StocksTable = () => {
  const { t } = useTranslation();
  const { marketQuoteClient } = useJobSalaryContext();
  const { isDemoMode } = DemoContext.useDemo();

  const quotes = useMemo(() => {
    return ["BTC/USD", "ETH/USD", "SOL/USD", "XLM/USD"].map((symbol) => marketQuoteClient!.getMarketQuote(symbol));
  }, [marketQuoteClient]);

  const columns: ColumnDef<MarketQuote>[] = [
    {
      accessorKey: "symbol",
      header: ({ header, table }) => StocksHeaderCell(header, "Crypto", table, "start"),
      size: 170,
      cell: (info) => (
        <TableCell
          align="start"
          className="h-full w-full py-2"
        >
          <Typography
            size="label-1"
            className="truncate font-weight-600"
          >
            {info.row.original.symbol}
          </Typography>
        </TableCell>
      ),
      meta: { label: "Crypto" },
    },
    {
      accessorKey: "price",
      header: ({ header, table }) => StocksHeaderCell(header, t("watchlist.filters.price", "Price"), table, "end"),
      size: 120,
      cell: (info) => (
        <div className="flex h-full w-full items-center justify-end truncate px-2-5 py-0!">
          <Typography
            size="label-1"
            className="whitespace-nowrap text-right"
          >
            <MarketQuotePrice quote={info.row.original} />
          </Typography>
        </div>
      ),
      meta: { label: t("watchlist.filters.price", "Price") },
    },
  ];

  return (
    <div className="flex h-full w-full flex-[1_1_0] flex-col flex-wrap">
      <div className="flex w-full flex-[1_1_0] flex-col items-center justify-center">
        <div
          className={cx(
            "flex w-full flex-[0_0_0] flex-col items-center justify-center gap-4 dd:px-0 px-2 pb-2",
            isDemoMode && "pt-1 t:pt-0",
          )}
        ></div>
        <VirtualizedTable
          data={quotes}
          columns={columns}
          sorting={[]}
          isLoading={false}
          onSortingChange={() => {}}
          className="h-full w-full flex-1"
          noBorder
          rowHeight={32}
          flexibleColumnId="symbol"
        />
      </div>
    </div>
  );
};

const StocksHeaderCell = (
  header: Header<any, unknown>,
  label: string,
  table: Table<MarketQuote>,
  align: "start" | "center" | "end",
) => {
  return (
    <WatchlistTableHeaderCell
      header={header}
      label={label}
      align={align}
      table={table}
    />
  );
};
